import { useCallback, useContext, useState } from 'react';
import { LOG_ACTIONS, logAction } from '@namespace/analytics';
import {
  asyncAttempt,
  COOKIES_KEYS,
  getCookie,
  LOCAL_STORAGE_KEYS,
  setLocalStorageValue
} from '@namespace/helpers';
import { useI18n } from '@namespace/i18n';
import { useNotifications } from '@namespace/notifications';
import { UserContext } from '@namespace/user';
import {
  deleteBankCard,
  putCVVToCache,
  saveBankCard
} from '../../api/bankCard';
import { DeleteBankCardParams } from '../../api/bankCard/types';
import { PAN_TYPE } from '../../constants';
import { STORE_PROPS, usePaymentsStore } from '../../store';
import { ApiError } from '../../types/apiError';
import {
  getErrorMsg,
  getExtPaySysErrorMsg,
  isPanType,
  isWalletType
} from '../../utils';
import { useBCList } from '../useBCList';
import { useBCSelectedCard } from '../useBCSelectedCard';

export type NewCard = {
  cardNumber: string;
  expiryDate: string;
  description: string;
  cvv?: string;
  cardHash?: string;
};

export type UpdateCardParams = {
  expiryDate: string;
  description: string;
};

export const useBCManagement = (isWithdraw?: boolean) => {
  const { t } = useI18n();
  const { errorNotification, successNotification } = useNotifications();

  const [user] = useContext(UserContext);
  const { id: userId, partnerId: accountingId } = user;

  const [ssid] = useState(getCookie(COOKIES_KEYS.PHPSESSID, null));
  const activeServiceId = usePaymentsStore(STORE_PROPS.ACTIVE_SERVICE_ID);

  const { cardList, findCard, fetchCards, isCardsLoaded } = useBCList(
    userId,
    isWithdraw
  );

  const { selectedCard, setSelectedCard } = useBCSelectedCard(
    isCardsLoaded,
    cardList
  );

  const addCard = useCallback(
    async (card = {} as NewCard) => {
      const { cardNumber, expiryDate, description } = card;
      const [month, year] = expiryDate.split('/');
      const expYear = year.length === 2 ? `20${year}` : year;
      const [error, result] = await asyncAttempt(saveBankCard, {
        card_number: cardNumber.replace(/[\s]/g, ''),
        service_id: activeServiceId as unknown as number,
        exp_month: month,
        exp_year: expYear,
        description
      });
      if (error) {
        const { errorCode } = error;
        const errorMsg = getExtPaySysErrorMsg(errorCode);
        errorNotification(t(errorMsg));
        logAction(LOG_ACTIONS.PANKEEPER_ADD_CARD_FAILED, {
          error: errorMsg
        });
        throw new Error(errorMsg);
      }
      logAction(LOG_ACTIONS.PANKEEPER_ADD_CARD_SUCCESS);

      return result;
    },
    [activeServiceId, errorNotification, t]
  );

  const getCardHash = useCallback(
    async (card: NewCard) => {
      const existingCard = findCard(card);
      const currentCard = existingCard ? { ...card, ...existingCard } : card;
      const { cardHash: hash, cvv } = currentCard;

      let cardHash = hash;

      try {
        if (!hash) {
          const { cardHash: fetchedHash } = (await addCard(currentCard)) as {
            cardHash: string;
          };
          cardHash = fetchedHash;
        }

        await fetchCards();
        if (cvv) {
          await putCVVToCache({
            cardHash,
            userId,
            accountingId,
            cvv,
            ssid
          });
        }
      } catch (error) {
        const errorMsg =
          (error as ApiError).message || getErrorMsg(error as never); // TODO: add typescript for getErrorMsg util
        errorNotification(t(errorMsg));
        throw new Error(errorMsg);
      }

      return { cardHash };
    },
    [
      findCard,
      fetchCards,
      userId,
      accountingId,
      ssid,
      addCard,
      errorNotification,
      t
    ]
  );

  const deleteCard = useCallback(async () => {
    if (selectedCard) {
      try {
        const { cardHash, cardNumber, type, accountId } = selectedCard;
        const walletTypeBody = {
          type: PAN_TYPE.WALLET,
          card_number: cardNumber
        };

        const panTypeBody = isPanType(type)
          ? { type: PAN_TYPE.PAN, card_hash: cardHash }
          : { type: PAN_TYPE.PAN_TOKEN, account_id: accountId };
        const data = isWalletType(type) ? walletTypeBody : panTypeBody;
        await deleteBankCard(data as DeleteBankCardParams); // TODO: create type assertion function
        setLocalStorageValue(LOCAL_STORAGE_KEYS.CARD_HASH_KEY, '');
        successNotification(t('web.pankeeper.delete.resultNTF'));
        await fetchCards();
      } catch (e) {
        const { errorCode, error = {} } = e as ApiError;
        const errorMsg = getExtPaySysErrorMsg(errorCode);
        errorNotification(t(errorMsg));
        logAction(LOG_ACTIONS.PANKEEPER_DELETE_CARD_FAILED, {
          error
        });
      }
    }
  }, [selectedCard, successNotification, t, fetchCards, errorNotification]);

  const updateCard = useCallback(
    async ({ expiryDate, description }: UpdateCardParams) => {
      if (selectedCard) {
        try {
          const { cardHash, cardNumber, type, cardHolderName } = selectedCard;
          const [month, year] = expiryDate.split('/');
          const expYear = year.length === 2 ? `20${year}` : year;
          const data = {
            service_id: activeServiceId as unknown as number,
            exp_month: month,
            exp_year: expYear,
            card_holder_name: cardHolderName,
            description
          };
          const dataByType = isWalletType(type)
            ? { card_number: cardNumber as string }
            : { card_hash: cardHash };
          await saveBankCard({ ...data, ...dataByType });
          successNotification(t('web.pankeeper.updateNTF'));
          await fetchCards();
        } catch (e) {
          const { errorCode } = e as ApiError;
          const errorMsg = getExtPaySysErrorMsg(errorCode);
          errorNotification(t(errorMsg));
        }
      }
    },
    [
      selectedCard,
      activeServiceId,
      successNotification,
      t,
      fetchCards,
      errorNotification
    ]
  );

  return {
    addCard,
    getCardHash,
    deleteCard,
    updateCard,
    setSelectedCard,
    selectedCard,
    cardList,
    isCardsLoaded
  };
};
